import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import {
  ShieldAlert, ShieldCheck, Upload, Eye, Trash2, Loader2, CheckCircle2,
  AlertTriangle, CreditCard, Landmark, CalendarClock, FileWarning,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { loanProfilingApi } from '@/services/api';
import { SectionHeading } from './CockpitUI';

const CC_TYPES = ['10', '31', '35']; // credit card account-type codes
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const rupees = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

const isClosed = (a) => {
  const s = String(a.Account_Status || '');
  const dc = a.Date_Closed || a.DateClosed || a.dateClosed || a.date_closed || a.close_date || '';
  return ['13', '14', '15'].includes(s) || a.credit_status === 'Closed'
    || (String(dc).trim() !== '' && String(dc).trim() !== '0');
};
const maxDPD = (a) => {
  const hist = a.CAIS_Account_History || a.payment_history || [];
  const histMax = hist.reduce((m, h) => Math.max(m, parseInt(h.Days_Past_Due, 10) || 0), 0);
  return Math.max(histMax, parseInt(a.Days_Past_Due, 10) || 0);
};
const lender = (a) => a.Subscriber_Name || a.member || a.institution || a.memberShortName || 'Lender';

const DOC_LABELS = {
  foreclosure_letter: 'Loan Foreclosure Letter / Statement',
  loan_closure_letter: 'Loan Closure Letter',
  existing_loan_statement: 'Existing Loan Account Statement',
  noc: 'NOC / No-Dues Certificate',
  cc_closure_letter: 'Credit Card Closure Letter / Statement',
  late_payment_justification: 'Late Payment Justification Letter',
  payment_regularization_proof: 'Overdue Payment / Regularisation Proof',
  settlement_letter: 'Settlement / Closure Letter',
};

const TONE = {
  amber: { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', label: 'Review' },
  rose: { border: 'border-rose-200', bg: 'bg-rose-50', text: 'text-rose-600', badge: 'bg-rose-100 text-rose-700', label: 'High' },
};

// Derive risk findings + required justification docs from the parsed bureau report.
export function analyzeCreditRisk(report) {
  const accounts = report?.accounts || report?.CAIS_Account?.CAIS_Account_DETAILS || [];
  const open = accounts.filter((a) => !isClosed(a));
  const bal = (a) => num(a.Current_Balance || a.current_balance);

  const activeLoans = open.filter((a) => !CC_TYPES.includes(String(a.Account_Type)) && bal(a) > 0);
  const activeCards = open.filter((a) => CC_TYPES.includes(String(a.Account_Type)) && bal(a) > 0);
  const overdueAccts = accounts.filter((a) => num(a.Amount_Past_Due || a.amount_overdue) > 0);
  const latePayAccts = accounts.filter((a) => maxDPD(a) > 0);
  const writtenOff = accounts.filter((a) => num(a.Written_Off_Amt_Total) > 0 || ['78', '89'].includes(String(a.Account_Status)));
  const settled = accounts.filter((a) => num(a.Settlement_Amount) > 0);

  const findings = [];
  if (activeLoans.length) {
    const t = activeLoans.reduce((s, a) => s + bal(a), 0);
    findings.push({
      key: 'active_loans', severity: 'amber', icon: Landmark,
      title: `${activeLoans.length} active loan${activeLoans.length > 1 ? 's' : ''} running`,
      detail: `Outstanding ~${rupees(t)} across ${activeLoans.map(lender).join(', ')}. Existing EMIs reduce eligibility — the bank needs closure proof or a justification for the ongoing loan(s).`,
      docs: ['foreclosure_letter', 'existing_loan_statement', 'noc'],
    });
  }
  if (activeCards.length) {
    const t = activeCards.reduce((s, a) => s + bal(a), 0);
    findings.push({
      key: 'active_cards', severity: 'amber', icon: CreditCard,
      title: `${activeCards.length} credit card${activeCards.length > 1 ? 's' : ''} with outstanding`,
      detail: `Card dues ~${rupees(t)}. Provide a closure letter / latest statement so the bank can assess the true obligation.`,
      docs: ['cc_closure_letter', 'noc'],
    });
  }
  if (latePayAccts.length) {
    const worst = Math.max(...latePayAccts.map(maxDPD));
    findings.push({
      key: 'late_payments', severity: worst >= 90 ? 'rose' : 'amber', icon: CalendarClock,
      title: `Late payments on ${latePayAccts.length} account${latePayAccts.length > 1 ? 's' : ''} (worst ${worst} DPD)`,
      detail: 'Delayed EMIs / dues found in payment history. A written justification (reason + proof of regularisation) helps the bank reconsider under internal approvals.',
      docs: ['late_payment_justification'],
    });
  }
  if (overdueAccts.length) {
    const t = overdueAccts.reduce((s, a) => s + num(a.Amount_Past_Due || a.amount_overdue), 0);
    findings.push({
      key: 'overdue', severity: 'rose', icon: AlertTriangle,
      title: `Currently overdue on ${overdueAccts.length} account${overdueAccts.length > 1 ? 's' : ''}`,
      detail: `Amount overdue ~${rupees(t)}. Clear it and attach regularisation / payment proof — active overdue usually blocks approval until justified.`,
      docs: ['payment_regularization_proof', 'late_payment_justification'],
    });
  }
  if (writtenOff.length) {
    findings.push({
      key: 'written_off', severity: 'rose', icon: FileWarning,
      title: `${writtenOff.length} written-off account${writtenOff.length > 1 ? 's' : ''}`,
      detail: `Write-off(s) reported by ${writtenOff.map(lender).join(', ')}. Provide closure / settlement proof + a justification for the internal-approval note.`,
      docs: ['settlement_letter', 'noc', 'late_payment_justification'],
    });
  }
  if (settled.length) {
    findings.push({
      key: 'settled', severity: 'amber', icon: FileWarning,
      title: `${settled.length} settled account${settled.length > 1 ? 's' : ''}`,
      detail: 'Settlement reported on the bureau. Attach the settlement letter + NOC to demonstrate closure.',
      docs: ['settlement_letter', 'noc'],
    });
  }

  const docTypes = [];
  findings.forEach((f) => f.docs.forEach((d) => { if (!docTypes.includes(d)) docTypes.push(d); }));
  return { findings, docTypes };
}

const DocRow = ({ leadId, docType, documents, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const existing = documents.find((d) => d.doc_type === docType);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await loanProfilingApi.uploadDocument(leadId, docType, file);
      toast.success(`${DOC_LABELS[docType] || docType} uploaded`);
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!existing) return;
    setBusy(true);
    try {
      await loanProfilingApi.deleteDocument(leadId, existing.id);
      toast.success('Removed');
      if (onChanged) await onChanged();
    } catch { toast.error('Delete failed'); } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-4 py-2.5" data-testid={`risk-doc-${docType}`}>
      <div className="flex items-center gap-2 min-w-0">
        {existing ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Upload className="w-4 h-4 text-slate-400 shrink-0" />}
        <span className="text-sm text-slate-700 truncate">{DOC_LABELS[docType] || docType}</span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {existing && (
          <>
            <a href={loanProfilingApi.fileUrl(leadId, existing.id)} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm" data-testid={`risk-doc-view-${docType}`}><Eye className="w-4 h-4" /></Button>
            </a>
            <Button variant="ghost" size="sm" onClick={del} disabled={busy} data-testid={`risk-doc-del-${docType}`}>
              <Trash2 className="w-4 h-4 text-rose-500" />
            </Button>
          </>
        )}
        <input ref={inputRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`risk-doc-input-${docType}`} />
        <Button variant={existing ? 'outline' : 'default'} size="sm" onClick={() => inputRef.current?.click()} disabled={busy}
          className={existing ? '' : 'bg-blue-600 hover:bg-blue-700'} data-testid={`risk-doc-upload-${docType}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : existing ? 'Replace' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

export const CreditRiskJustification = ({ leadId, report, documents = [], onChanged }) => {
  const { findings, docTypes } = analyzeCreditRisk(report);
  const collected = docTypes.filter((d) => documents.some((x) => x.doc_type === d)).length;

  return (
    <div className="mt-8" data-testid="credit-risk-justification">
      <SectionHeading hint="Auto-detected from the bureau report — collect justification docs so the bank can reconsider under internal approvals.">
        <span className="inline-flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-600" /> Risk Analysis &amp; Justification</span>
      </SectionHeading>

      {findings.length === 0 ? (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="risk-clean">
          <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
          <div>
            <p className="font-semibold text-emerald-800">No adverse credit signals detected</p>
            <p className="text-sm text-emerald-700">No existing loans, overdue or late payments found — no justification documents required.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5" data-testid="risk-summary">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-semibold">{findings.length} risk factor{findings.length > 1 ? 's' : ''} found.</span>{' '}
              Collect the justification documents below so the bank can reconsider the case under internal approvals.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            {findings.map((f) => {
              const Icon = f.icon;
              const t = TONE[f.severity];
              return (
                <div key={f.key} className={`bg-white border ${t.border} rounded-xl p-4`} data-testid={`risk-finding-${f.key}`}>
                  <div className="flex items-start gap-3">
                    <span className={`w-9 h-9 rounded-lg ${t.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${t.text}`} />
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 text-sm">{f.title}</p>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${t.badge}`}>{t.label}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{f.detail}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {f.docs.map((d) => (
                          <span key={d} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{DOC_LABELS[d] || d}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-5" data-testid="risk-doc-collection">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-800">Justification documents to collect</p>
              <span className="text-xs font-medium text-slate-500" data-testid="risk-doc-progress">{collected}/{docTypes.length} collected</span>
            </div>
            <div className="space-y-2">
              {docTypes.map((d) => (
                <DocRow key={d} leadId={leadId} docType={d} documents={documents} onChanged={onChanged} />
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">Stored with the loan file — these also appear under the “Existing Loan” group in the Documents step.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default CreditRiskJustification;
